/* Le fil d'activité : ce qu'on y écrit, et ce qu'on en lit.
 *
 * Trois natures qui ne se mélangent pas. Une note est dite par quelqu'un. Un
 * changement est constaté par l'app, jamais saisi — c'est la trace, et elle ne
 * se retouche pas. Une activité est un engagement : elle a une échéance, et
 * elle est en retard ou elle ne l'est pas.
 */

import type { EntreeJournal, EtatActivite, NatureJournal } from "@/types/metier";

export const NATURES: { cle: NatureJournal; nom: string }[] = [
  { cle: "note", nom: "Notes" },
  { cle: "activite", nom: "À faire" },
  { cle: "changement", nom: "Historique" },
];

/** Les champs qu'on suit : ceux dont le changement intéresse quelqu'un. */
export const SUIVIS: Record<string, Record<string, string>> = {
  clients: {
    nom: "Nom", type: "Type", segment: "Segment", ville: "Ville", pays: "Pays",
    tva: "N° TVA", conditions_paiement: "Conditions de paiement",
    limite_encours: "Limite d'encours", assurance_credit: "Assurance-crédit",
    confiance: "Confiance", incoterm: "Incoterm", agent_id: "Agent",
    commission_defaut: "Commission par défaut", part_agent_defaut: "Part agent par défaut",
  },
  usines: {
    nom: "Nom", categorie: "Catégorie", ville: "Ville", pays: "Pays",
    prefixe_facture: "Préfixe de facture", actif: "Actif", incoterm: "Incoterm",
    conditions_paiement: "Conditions de paiement", delai_production: "Délai de production",
  },
  produits: {
    nom: "Désignation", gamme: "Gamme", conditionnement: "Conditionnement",
    prix: "Prix de vente", actif: "Actif", code_douane: "Code douane",
    origine: "Origine", dluo: "DLUO", allergenes: "Allergènes",
  },
  ventes: { etape: "Étape", montant: "Montant", contact: "Contact" },
  profils: { nom: "Nom", niveau: "Niveau", actif: "Actif" },
};

export function nomChamp(sujet: string, champ: string): string {
  return SUIVIS[sujet]?.[champ] ?? champ;
}

/** Ce qui a bougé entre deux versions d'un enregistrement, et rien d'autre. */
export interface Changement {
  champ: string;
  avant: string | null;
  apres: string | null;
}

export function changements(
  sujet: string,
  avant: Record<string, unknown>,
  apres: Record<string, unknown>,
): Changement[] {
  const suivis = SUIVIS[sujet];
  if (!suivis) return [];
  const sorties: Changement[] = [];
  for (const champ of Object.keys(suivis)) {
    if (!(champ in apres)) continue;
    const a = normalise(avant[champ]);
    const b = normalise(apres[champ]);
    if (a === b) continue;
    sorties.push({ champ, avant: a, apres: b });
  }
  return sorties;
}

function normalise(v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v ? "oui" : "non";
  return String(v);
}

/** Une activité est en retard quand son échéance est passée et qu'elle reste à faire. */
export function enRetard(e: Pick<EntreeJournal, "nature" | "etat" | "echeance">,
                         aujourdhui = new Date().toISOString().slice(0, 10)): boolean {
  return e.nature === "activite" && e.etat === "a_faire"
    && Boolean(e.echeance) && (e.echeance as string) < aujourdhui;
}

/** Celles qui attendent, les plus urgentes d'abord. */
export function aFaire(entrees: readonly EntreeJournal[]): EntreeJournal[] {
  return entrees
    .filter((e) => e.nature === "activite" && e.etat === "a_faire")
    .sort((a, b) => (a.echeance ?? "9999").localeCompare(b.echeance ?? "9999"));
}

export const ETATS: Record<EtatActivite, string> = {
  a_faire: "à faire",
  faite: "faite",
  annulee: "annulée",
};

/** Le fil, du plus récent au plus ancien, filtré sur une nature s'il y a lieu. */
export function fil(
  entrees: readonly EntreeJournal[],
  nature: NatureJournal | null,
): EntreeJournal[] {
  return entrees
    .filter((e) => !nature || e.nature === nature)
    .sort((a, b) => b.cree_le.localeCompare(a.cree_le));
}

/** Une phrase qui dit ce qui s'est passé, pour un changement. */
export function phrase(e: EntreeJournal): string {
  if (e.nature !== "changement") return e.texte ?? "";
  const quoi = nomChamp(e.sujet, e.champ ?? "");
  if (e.avant === null) return `${quoi} renseigné : ${e.apres}`;
  if (e.apres === null) return `${quoi} effacé (était ${e.avant})`;
  return `${quoi} : ${e.avant} → ${e.apres}`;
}
